// Malware config extraction (controller configextract, run at teardown over the sample and its memory dumps): the
// config-report.json artifact once it loads, else the config.extracted events (one per finding, without version,
// campaign, botnet, keys and strings).
//   configFindingsOfEvents(events) → ConfigFinding[]
//   configOf(report?, events) → ConfigView   findings and extractor runs plus their merge: families, C2, URLs, mutexes,
//                                            paths, ATT&CK ids (deduplicated, case-insensitive, first spelling kept),
//                                            key count and the highest confidence
import type { ConfigExtractorRun, ConfigFinding, ConfigReport, Event } from "@/types";

export const listOf = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean) : []);

function uniq(xs: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) {
    const k = x.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

const confidenceOf = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.min(100, Math.round(n)) : undefined;
};

export function configFindingsOfEvents(events: readonly Event[]): ConfigFinding[] {
  const out: ConfigFinding[] = [];
  for (const e of events) {
    if (e.kind !== "config.extracted") continue;
    const f = e.fields ?? {};
    // The off-host extractor's eventbus payload nests the IoC lists
    // under a `c2` subobject ({c2, urls, mutex}) and the per-family
    // meta under `meta` ({capabilities, keys, strings}). api.ts
    // spreads the whole payload into Event.fields, so unwrap both
    // here. Falls back to flat lookup for legacy producers.
    const c2doc = typeof f.c2 === "object" && f.c2 && !Array.isArray(f.c2) ? (f.c2 as Record<string, unknown>) : undefined;
    const meta = typeof f.meta === "object" && f.meta ? (f.meta as Record<string, unknown>) : undefined;
    // The event's `raw` field is doubly-nested from the MACO shim's
    // Finding.other.raw plumbing — unwrap one level if present so the
    // UI sees the parser's actual {key: value} dump rather than
    // {raw: {key: value}}. Rust runner also emits {raw:{raw:{...}}}.
    const rawRoot = typeof f.raw === "object" && f.raw && !Array.isArray(f.raw) ? (f.raw as Record<string, unknown>) : undefined;
    const raw = rawRoot && typeof rawRoot.raw === "object" && rawRoot.raw && !Array.isArray(rawRoot.raw)
      ? (rawRoot.raw as Record<string, unknown>)
      : rawRoot;
    out.push({
      extractor: typeof f.extractor === "string" && f.extractor.trim() ? f.extractor.trim() : "extractor",
      family: typeof f.family === "string" && f.family.trim() ? f.family.trim() : undefined,
      confidence: confidenceOf(f.confidence),
      c2: listOf(c2doc?.c2 ?? f.c2),
      urls: listOf(c2doc?.urls ?? f.urls),
      mutex: listOf(c2doc?.mutex ?? f.mutex),
      paths: listOf(f.paths),
      capabilities: listOf(meta?.capabilities ?? f.capabilities),
      rule_refs: listOf(f.rule_refs),
      raw: raw ?? null,
    });
  }
  return out;
}

export type ConfigView = {
  source: "report" | "events";
  findings: ConfigFinding[];
  /** per-extractor outcome; only the report carries it */
  extractors: ConfigExtractorRun[];
  families: string[];
  c2: string[];
  urls: string[];
  mutex: string[];
  paths: string[];
  capabilities: string[];
  keys: number;
  confidence: number | null;
};

export function configOf(report: ConfigReport | undefined, events: readonly Event[]): ConfigView {
  // Merge both sources, deduped by (extractor, family). The backend
  // config-report.json artifact is built from the OLD extractors
  // (floss, maco) that run inside the controller — our off-host
  // Rust registry publishes directly to Kafka as config.extracted,
  // so when only the Rust runner finds a family the report lists it
  // nowhere. Favour the event's richer per-finding IoC lists; fall
  // back to the report when the event is missing a family.
  const reportFindings = report?.findings ?? [];
  const eventFindings = configFindingsOfEvents(events);
  const byKey = new Map<string, ConfigFinding>();
  const addOrMerge = (f: ConfigFinding) => {
    const key = `${(f.extractor ?? "").toLowerCase()}|${(f.family ?? "").toLowerCase()}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, f);
      return;
    }
    // Richer wins per list; otherwise keep the first. The report's
    // ConfigFinding types fields as `T[] | null`, event findings as
    // `T[] | undefined` — pick normalizes to T[].
    const pick = <T>(a: T[] | null | undefined, b: T[] | null | undefined): T[] => {
      const al = a?.length ?? 0;
      const bl = b?.length ?? 0;
      return (al >= bl ? a : b) ?? [];
    };
    byKey.set(key, {
      ...prev,
      family: prev.family ?? f.family,
      confidence: prev.confidence ?? f.confidence,
      c2: pick(prev.c2, f.c2),
      urls: pick(prev.urls, f.urls),
      mutex: pick(prev.mutex, f.mutex),
      paths: pick(prev.paths, f.paths),
      capabilities: pick(prev.capabilities, f.capabilities),
      rule_refs: pick(prev.rule_refs, f.rule_refs),
      // Richer raw wins (more keys). The event's raw is the parser's
      // full dump; the report's raw is sometimes a bare family hint.
      raw: (Object.keys(prev.raw ?? {}).length >= Object.keys(f.raw ?? {}).length ? prev.raw : f.raw) ?? null,
    });
  };
  for (const f of reportFindings) addOrMerge(f);
  for (const f of eventFindings) addOrMerge(f);
  const findings = [...byKey.values()];
  const merged = (pick: (f: ConfigFinding) => unknown) => uniq(findings.flatMap((f) => listOf(pick(f))));
  const confidences = findings.map((f) => confidenceOf(f.confidence)).filter((c): c is number => c !== undefined);
  return {
    source: reportFindings.length ? "report" : "events",
    findings,
    extractors: report?.extractors ?? [],
    families: uniq(findings.map((f) => f.family?.trim() ?? "").filter(Boolean)),
    c2: merged((f) => f.c2),
    urls: merged((f) => f.urls),
    mutex: merged((f) => f.mutex),
    paths: merged((f) => f.paths),
    capabilities: merged((f) => f.capabilities).map((c) => c.toUpperCase()),
    keys: findings.reduce((n, f) => n + (f.keys?.length ?? 0), 0),
    confidence: confidences.length ? Math.max(...confidences) : null,
  };
}
